import type { IncidentPosition } from 'hooks/incident/useRoster';
import type { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import {
  positionMatchesRosterFilters,
  positionMatchesSearch,
  rosterEntryMatchesSearch,
  type RosterFilters,
} from './rosterPositionFilter';

export type RosterListOccupant = {
  member: RosterEntry;
  /** Set for people seated in a later planned period. */
  periodLabel: string | null;
  later: boolean;
};

export type RosterListNode = {
  position: IncidentPosition;
  occupants: RosterListOccupant[];
  children: RosterListNode[];
  /** Every seat below this one that survived the filters. */
  descendantCount: number;
};

export type RosterListTree = {
  roots: RosterListNode[];
  /** Seats to open so search hits are visible without clicking down to them. */
  autoOpenIds: string[];
  /** Every seat in the tree, for Expand all. */
  allIds: string[];
};

function occupantsByPosition(
  shown: RosterEntry[],
  later: RosterEntry[],
  labels: Record<string, string>
): Map<string, RosterListOccupant[]> {
  const byPosition = new Map<string, RosterListOccupant[]>();
  const add = (member: RosterEntry, isLater: boolean) => {
    (member.incidentPositions ?? []).forEach((seat) => {
      const list = byPosition.get(seat.positionId) ?? [];
      if (list.some((entry) => entry.member.userId === member.userId)) return;
      list.push({
        member,
        periodLabel: isLater ? (labels[member.userId] ?? null) : null,
        later: isLater,
      });
      byPosition.set(seat.positionId, list);
    });
  };
  shown.forEach((member) => add(member, false));
  later.forEach((member) => add(member, true));
  return byPosition;
}

/**
 * The roster as nested seats for the List view. Positions and categories narrow
 * the tree like the chart; empty seats drop out unless the occupancy filter
 * keeps them; search keeps a hit's ancestors so it stays reachable and keeps
 * everything under a hit.
 */
export function buildRosterListTree({
  positions,
  shown,
  later = [],
  labels = {},
  filters,
}: {
  positions: IncidentPosition[];
  shown: RosterEntry[];
  later?: RosterEntry[];
  labels?: Record<string, string>;
  filters: RosterFilters;
}): RosterListTree {
  const ids = new Set(positions.map((position) => position.id));
  const childrenOf = new Map<string, IncidentPosition[]>();
  const roots: IncidentPosition[] = [];
  positions.forEach((position) => {
    if (position.parent_id && ids.has(position.parent_id)) {
      const list = childrenOf.get(position.parent_id) ?? [];
      list.push(position);
      childrenOf.set(position.parent_id, list);
    } else {
      roots.push(position);
    }
  });

  const occupants = occupantsByPosition(shown, later, labels);
  const structural = { ...filters, search: '' };
  const term = filters.search.trim();
  const includeEmpty = filters.occupancy.includes('empty');
  const autoOpenIds: string[] = [];
  const allIds: string[] = [];
  const visiting = new Set<string>();

  const build = (
    position: IncidentPosition,
    searchSatisfied: boolean
  ): RosterListNode | null => {
    if (visiting.has(position.id)) return null;
    visiting.add(position.id);
    const seated = occupants.get(position.id) ?? [];
    const hit =
      searchSatisfied ||
      positionMatchesSearch(position, term) ||
      seated.some((entry) => rosterEntryMatchesSearch(entry.member, term));
    const children = (childrenOf.get(position.id) ?? [])
      .map((child) => build(child, hit))
      .filter((node): node is RosterListNode => node !== null);
    visiting.delete(position.id);

    const keepSelf =
      hit &&
      positionMatchesRosterFilters(position, structural, positions) &&
      (seated.length > 0 || includeEmpty);
    if (!keepSelf && children.length === 0) return null;

    if (term && !searchSatisfied && children.length > 0) {
      autoOpenIds.push(position.id);
    }
    allIds.push(position.id);
    return {
      position,
      occupants: seated,
      children,
      descendantCount: children.reduce(
        (total, child) => total + 1 + child.descendantCount,
        0
      ),
    };
  };

  return {
    roots: roots
      .map((position) => build(position, !term))
      .filter((node): node is RosterListNode => node !== null),
    autoOpenIds,
    allIds,
  };
}
